import { useMemo } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { scrollModePlugin } from '@react-pdf-viewer/scroll-mode';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { themePlugin } from '@react-pdf-viewer/theme';
import { zoomPlugin } from '@react-pdf-viewer/zoom';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import { isMobile } from '../../../../../utils/utils';
import * as S from './styles';

interface PdfPanelProps {
    url: string;
    title?: string;
    defaultScale?: number;
    onDocumentLoad?: (numPages: number) => void;
    onPageChange?: (currentPage: number, numPages: number) => void;
    onFallbackLoad?: () => void;
}

const renderMinimalToolbar = (Toolbar: any) => (
    <Toolbar>
        {(slots: any) => {
            const {
                ZoomOut,
                Zoom,
                ZoomIn,
                GoToPreviousPage,
                GoToNextPage,
                Print,
                Download,
            } = slots;

            return (
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        width: '100%',
                        backgroundColor: '#242424',
                        padding: '4px',
                        ['--rpv-core__minimal-button-color' as any]: '#ffffff',
                        ['--rpv-toolbar__label-color' as any]: '#ffffff',
                        ['--rpv-core__minimal-button-hover-background-color' as any]:
                            'rgba(255, 255, 255, 0.1)',
                    }}
                >
                    <style>{`
                        .rpv-core__minimal-button,
                        .rpv-core__icon {
                            color: #ffffff !important;
                        }
                        .rpv-core__minimal-button:disabled {
                            color: #555555 !important;
                            cursor: not-allowed;
                        }
                    `}</style>
                    <div style={{ display: 'flex', width: '100%' }}>
                        <div style={{ display: 'flex', flex: '1', justifyContent: 'flex-start' }}>
                            <div style={{ padding: '0 2px' }}><ZoomOut /></div>
                            <div style={{ padding: '0 2px' }}><Zoom /></div>
                            <div style={{ padding: '0 2px' }}><ZoomIn /></div>
                        </div>

                        <div style={{ display: 'flex', flex: '1', justifyContent: 'center' }}>
                            <div style={{ padding: '0 2px' }}><GoToPreviousPage /></div>
                            <div style={{ padding: '0 2px' }}><GoToNextPage /></div>
                        </div>

                        <div style={{ display: 'flex', flex: '1', justifyContent: 'flex-end' }}>
                            <div style={{ padding: '0 2px' }}><Download /></div>
                            <div style={{ padding: '0 2px' }}><Print /></div>
                        </div>
                    </div>
                </div>
            );
        }}
    </Toolbar>
);

export const PdfPanel = ({
    url,
    title = 'Documento',
    defaultScale,
    onDocumentLoad,
    onPageChange,
    onFallbackLoad,
}: PdfPanelProps) => {
    const scrollModePluginInstance = scrollModePlugin();
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;
    const themePluginInstance = themePlugin();
    const zoomPluginInstance = zoomPlugin({ enableShortcuts: true });

    const pdfWorkerUrl = useMemo(() => {
        try {
            // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
            const pdfjsVersion = require('pdfjs-dist/package.json')?.version;
            return `https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`;
        } catch {
            return 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
        }
    }, []);

    const renderFallbackIframe = () => {
        const iframeSrc = isMobile()
            ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`
            : url;
        return <S.DocumentFrame src={iframeSrc} title={title} onLoad={onFallbackLoad} />;
    };

    return (
        <S.PanelWrapper>
            {renderMinimalToolbar(Toolbar)}
            <S.ViewerArea>
                <Worker workerUrl={pdfWorkerUrl}>
                    <Viewer
                        theme="dark"
                        fileUrl={url}
                        defaultScale={defaultScale ?? (isMobile() ? 0.6 : 1.0)}
                        plugins={[
                            scrollModePluginInstance,
                            toolbarPluginInstance,
                            themePluginInstance,
                            zoomPluginInstance,
                        ]}
                        onDocumentLoad={(e) => onDocumentLoad?.(e.doc.numPages)}
                        onPageChange={(e) => onPageChange?.(e.currentPage, e.doc.numPages)}
                        renderError={() => renderFallbackIframe()}
                    />
                </Worker>
            </S.ViewerArea>
        </S.PanelWrapper>
    );
};
